import React, { useEffect, useState } from 'react';
import { Alert, Checkbox, Spin, Table, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  fetchOrderStockStatus,
  type OrderStockLine,
  type OrderStockStatus,
} from '../api/orderStockStatusApi';
import { formatUnit } from '../../../utils/units';
import {
  getConfirmationNoticeDefaults,
  isRecipientSelected,
  noticeRecipientTitle,
  withRecipientSelected,
  type OrderConfirmationNoticeSelection,
} from '../confirmationNoticeSelection';
import { formatLocalizedQuantity } from '../../../utils/numberFormatting';
import { activeSupplierDraft, supplierDraftStatusKey } from '../process/orderProcessFacts';

const { Text } = Typography;

export type { OrderConfirmationNoticeSelection };
export { getConfirmationNoticeDefaults };

const formatQuantity = (value: number | null, unit: string): string => (
  value == null ? '—' : `${formatLocalizedQuantity(value)} ${formatUnit(unit)}`
);

interface OrderConfirmationNoticeSectionProps {
  orderId: string;
  value: OrderConfirmationNoticeSelection | null;
  onChange: (value: OrderConfirmationNoticeSelection) => void;
}

/**
 * Confirmation part of the status dialog (makieta ZK „Sprawdzenie magazynu”,
 * „Potwierdzenie i powiadomienia”, „Szkic zamówienia do dostawcy”; decision
 * 2026-10-04 pkt 4–6). Shown only when the chosen status starts a
 * confirmation cycle. Reads GET /orders/:id/stock-status (quantities only);
 * the default ticks come from the WP4 recipient settings. Nothing here blocks
 * the confirmation.
 */
const OrderConfirmationNoticeSection: React.FC<OrderConfirmationNoticeSectionProps> = ({ orderId, value, onChange }) => {
  const { t } = useTranslation(['orders']);
  const [status, setStatus] = useState<OrderStockStatus | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setStatus(null);
    setFailed(false);
    fetchOrderStockStatus(orderId)
      .then((loaded) => {
        if (cancelled) return;
        setStatus(loaded);
        if (!value) onChange({ ...getConfirmationNoticeDefaults(loaded), customer: false });
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  if (failed) {
    return <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={t('orders:confirmationNotice.loadError')} />;
  }
  if (!status) return <Spin style={{ display: 'block', margin: '16px auto' }} />;

  // The customer is mailed from the offer step (with a preview), never from a status change (owner 2026-10-05).
  const selection = value || { ...getConfirmationNoticeDefaults(status), customer: false };
  const people = status.noticeRecipients;
  const logisticsLabel = (name: string) => t('orders:confirmationNotice.logisticsPerson', { name });
  const missing = status.lines.filter((line) => line.state === 'MISSING');
  const draftExists = status.supplierDraft.state !== 'NONE';
  // A rejected draft / cancelled ZD stays (no new draft is created) but is no ZD (orderProcessFacts.activeSupplierDraft).
  const draftInactive = draftExists && !activeSupplierDraft(status);
  const draftStatusKey = supplierDraftStatusKey(status.supplierDraft);

  return (
    <div data-testid="order-confirmation-notice" style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
      <Text strong>{t('orders:confirmationNotice.stockTitle')}</Text>
      <Table<OrderStockLine>
        size="small"
        pagination={false}
        rowKey="orderProductId"
        dataSource={status.lines}
        columns={[
          { title: t('orders:confirmationNotice.columns.product'), dataIndex: 'name' },
          {
            title: t('orders:confirmationNotice.columns.ordered'),
            align: 'right',
            render: (_: unknown, line) => formatQuantity(line.ordered, line.unit),
          },
          {
            title: t('orders:confirmationNotice.columns.onHand'),
            align: 'right',
            render: (_: unknown, line) => formatQuantity(line.onHand, line.unit),
          },
          {
            title: t('orders:confirmationNotice.columns.status'),
            render: (_: unknown, line) => (line.state === 'MISSING'
              ? <Tag color="orange">{t('orders:confirmationNotice.missing', { quantity: formatQuantity(line.missing, line.unit) })}</Tag>
              : line.state === 'UNKNOWN'
                ? <Tag>{t('orders:confirmationNotice.unknown')}</Tag>
                : <Tag color="green">{t('orders:confirmationNotice.available')}</Tag>),
          },
        ]}
      />
      {missing.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          message={draftInactive
            ? t('orders:confirmationNotice.draftInactive', { number: status.supplierDraft.supplierOrderNumber || '', status: draftStatusKey ? t(draftStatusKey) : '' })
            : draftExists
            ? t('orders:confirmationNotice.draftExists', { number: status.supplierDraft.supplierOrderNumber || '' })
            : t('orders:confirmationNotice.draftWillBeCreated')}
        />
      ) : (
        <Alert type="success" showIcon message={t('orders:confirmationNotice.allAvailable')} />
      )}

      <Text strong>{t('orders:confirmationNotice.recipientsTitle')}</Text>
      <div style={{ display: 'grid', gap: 6 }}>
        {people.map((person) => (
          <Checkbox
            key={person.userId}
            checked={isRecipientSelected(selection, person)}
            onChange={(event) => onChange(withRecipientSelected(selection, person, event.target.checked))}
            data-testid={`confirmation-notice-user-${person.userId}`}
          >
            <Text strong>{noticeRecipientTitle(person, people, logisticsLabel)}</Text>
            <div>
              <Text type="secondary">
                {t(person.role === 'OPERATIONS' ? 'orders:confirmationNotice.operationsHint' : 'orders:confirmationNotice.adminsHint')}
              </Text>
            </div>
          </Checkbox>
        ))}
      </div>
    </div>
  );
};

export default OrderConfirmationNoticeSection;
